import type { AutomationRunListParams } from './contracts'

export const automationsQueryKeys = {
  /** Prefix for every automations query: the list, each detail, runs and deliveries. */
  all: ['automations'] as const,
  list: ['automations', 'list'] as const,
  /** `GET /automations?deleted=true`: the trash, most recently deleted first. */
  deletedList: ['automations', 'list', 'deleted'] as const,
  detail: (automationId: string) => ['automations', 'detail', automationId] as const,
  /** Every version of one automation, newest first. */
  versions: (automationId: string) => ['automations', 'versions', automationId] as const,
  version: (automationId: string, versionId: string) =>
    ['automations', 'versions', automationId, versionId] as const,
  /** Prefix for every `GET /automations/runs` query, regardless of filters. */
  runLogAll: ['automations', 'run-log'] as const,
  /** `GET /automations/runs`: runs across every automation, by filter. */
  runLog: (params: AutomationRunListParams) => ['automations', 'run-log', params] as const,
  /** Prefix for one automation's runs and deliveries, regardless of paging. */
  runsAll: (automationId: string) => ['automations', 'runs', automationId] as const,
  runs: (automationId: string, params: { limit?: number; offset?: number } = {}) =>
    ['automations', 'runs', automationId, params] as const,
  /** One run, while the pages wait for it to finish. */
  run: (automationId: string, runId: string) =>
    ['automations', 'runs', automationId, runId] as const,
  runDeliveries: (automationId: string, runId: string, params: object = {}) =>
    ['automations', 'runs', automationId, runId, 'deliveries', params] as const,
  /** Where email from this environment goes (`GET /automations/email-settings`). */
  emailDelivery: ['automations', 'email-settings'] as const,
  /** What an email step can attach (`GET /automations/files/limits`). */
  fileLimits: ['automations', 'files', 'limits'] as const,
  /** The holidays, workweek and working hours schedules run on. */
  scheduleCalendar: ['automations', 'schedule-calendar'] as const,
  /** Preview of a saved automation (`POST /automations/{id}/preview`). */
  preview: (automationId: string, limit: number) =>
    ['automations', 'preview', automationId, limit] as const,
  /** Preview of one saved version (`POST /automations/{id}/versions/{versionId}/preview`). */
  versionPreview: (automationId: string, versionId: string, limit: number) =>
    ['automations', 'preview', automationId, 'versions', versionId, limit] as const,
  /** Preview of unsaved editor content (`POST /automations/preview`), keyed by the draft. */
  draftPreview: (draft: object, limit: number) =>
    ['automations', 'preview', 'draft', draft, limit] as const,
}
